/**
 * File: ChatRuntime.tsx
 * Description: Chat runtime setup for assistant-ui with real backend streaming integration
 * Author: System
 * Created: 2025-09-03
 * Last Modified: 2025-09-03
 */

import { 
  AssistantRuntimeProvider, 
  useLocalRuntime,
} from '@assistant-ui/react';
import { 
  type FC, 
  type PropsWithChildren, 
  createContext, 
  useCallback, 
  useContext, 
  useEffect, 
  useMemo,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { ChatActionsContext, type ChatActionsContextValue, type PendingChatAction, type ActionExecutionResult } from '../../hooks/useChatActions';
import { useProjectContext } from '../../hooks/useProjectContext';
import { adrService } from '../../services/adrService';
import { chatService } from '../../services/chatService';
import { contextManager } from '../../services/contextManager';
import { projectService } from '../../services/projectService';
import { streamingChatService } from '../../services/streamingChatService';
import { ADRDefinitionStatus, ADRDefinitionUpsert, ADRSource } from '../../types/adr';
import { ChatAction } from '../../types/chat';
import { logger } from '../../utils/logger';

import { 
  SearchWikiToolUI, 
  SearchGuidelinesToolUI, 
  SearchUserDocsToolUI,
  ReadBusinessRequirementsToolUI,
  ReadGeneratedContentToolUI,
  ReadProjectContextToolUI,
  SearchBrownfieldDocsToolUI,
  LookupApplicationIdsToolUI,
  ReadProjectADRsToolUI,
  ReadProjectInstructionsToolUI,
  FetchWebpageToolUI,
} from './GenericToolUI';
import { SelectProjectToolUI, SELECT_PROJECT_TOOL_NAME } from './SelectProjectToolUI';

interface ToolCall {
  tool_name: string;
  tool_input: string;
  tool_output: string;
}

interface AgentExecutionStep {
  agent_name?: string;
  reasoning?: string;
  tool_calls?: ToolCall[];
}

// New streaming format interfaces
interface ToolEvent {
  tool_name: string;
  input: string;
  output?: string;
  run_id: string;
  status: 'running' | 'completed' | 'error';
  error?: string;
}

interface ChatRuntimeProviderProps extends PropsWithChildren {
  projectId?: number | null;
}

// Create a context for clear history function
const ChatHistoryContext = createContext<{
    clearHistory?: () => void;
    isLoadingHistory?: boolean;
      }>({});

export const useChatHistory = () => useContext(ChatHistoryContext);

interface HistoryMessage {
  role: 'user' | 'assistant';
  content: string;
  id?: string;
}

/**
 * Hook to restore chat history when the chat opens
 */
const useChatHistoryRestore = (projectId: number | null) => {
  const [historyMessages, setHistoryMessages] = useState<HistoryMessage[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {return;}

    const restoreHistory = async () => {
      try {
        setIsLoadingHistory(true);
        
        const sessionData = await chatService.getOrCreateSession(projectId);
        
        setSessionId(sessionData.session_id);
        streamingChatService.setCurrentSessionId(sessionData.session_id);
        
        if (sessionData.has_history && sessionData.messages.length > 0) {
          // Convert backend messages to assistant-ui format
          const convertedMessages: HistoryMessage[] = sessionData.messages.map((msg, index) => ({
            role: msg.role,
            content: msg.content, // This is already a string from the backend
            id: msg.id || `${msg.role}-${index}`,
          }));
          
          setHistoryMessages(convertedMessages);
        } else {
          setHistoryMessages([]);
        }
      } catch (error) {
        logger.error('Failed to restore chat history:', error);
        setHistoryMessages([]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    restoreHistory();
  }, [projectId]);

  const clearHistory = async () => {
    if (!projectId) {
      return;
    }

    try {
      await chatService.clearUserChatHistory(projectId);
      setHistoryMessages([]);
    } catch (error) {
      logger.error('Failed to clear chat history:', error);
    }
  };

  return {
    historyMessages,
    isLoadingHistory,
    sessionId,
    clearHistory,
  };
};

/**
 * Build an assistant response that prompts the user to pick a project. Used
 * when the chat is opened without an active project (e.g. on the front page).
 */
const buildProjectSelectionResponse = async () => {
  const noProjectIntro =
    'You don\'t have a project selected yet. Pick one below to start ' +
    'chatting, and I\'ll switch to it automatically:';

  let projects: { id: number; title: string }[] = [];
  try {
    const allProjects = await projectService.getProjects();
    projects = allProjects.map((project) => ({
      id: project.id,
      title: project.title,
    }));
  } catch (error) {
    logger.error('Failed to load projects for chat selection:', error);
  }

  return {
    content: [
      {
        type: 'text' as const,
        text: noProjectIntro,
      },
      {
        type: 'tool-call' as const,
        toolCallId: `${SELECT_PROJECT_TOOL_NAME}_${Date.now()}`,
        toolName: SELECT_PROJECT_TOOL_NAME,
        args: { projects },
        result: '',
      },
    ],
  };
};

/**
 * Create chat adapter that integrates with our backend streaming chatService
 * Optimized for assistant-ui streaming with proper message accumulation
 */
const createChatAdapter = (
  projectId: number | null, 
  sessionId: string | null,
  onActions?: (_actions: ChatAction[]) => void,
) => ({
  async *run({ messages, abortSignal: _abortSignal }: any) {
    if (!projectId) {
      yield await buildProjectSelectionResponse();
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const userInput = extractUserInput(lastMessage);

    if (!userInput.trim()) {
      return;
    }

    try {
      const context = contextManager.getContext();
      const stream = streamingChatService.streamMessage(
        projectId,
        userInput.trim(),
        context,
        sessionId || undefined,
      );

      const contentParts: any[] = [];
      let currentTextPart = '';
      
      for await (const chunk of stream) {
        if (chunk.type === 'actions') {
          onActions?.(chunk.content as ChatAction[]);
          continue;
        }

        if (chunk.type === 'text') {
          // Accumulate text deltas for smoother streaming
          currentTextPart += chunk.content;
          
          // Yield immediately for real-time streaming
          yield {
            content: [
              ...contentParts,
              {
                type: 'text' as const,
                text: currentTextPart,
              },
            ],
          };
        } else if (chunk.type === 'tool-call') {
          // If there's accumulated text, finalize it before adding tool call
          if (currentTextPart) {
            contentParts.push({
              type: 'text' as const,
              text: currentTextPart,
            });
            currentTextPart = '';
          }
          
          const toolContent = processToolCall(chunk.content);
          if (toolContent) {
            contentParts.push(toolContent);
            
            // Yield with tool call included
            yield {
              content: [...contentParts],
            };
          }
        }
      }
      
      // Final yield with all content
      if (currentTextPart || contentParts.length > 0) {
        yield {
          content: [
            ...contentParts,
            ...(currentTextPart ? [{
              type: 'text' as const,
              text: currentTextPart,
            }] : []),
          ],
        };
      }
    } catch (error) {
      yield {
        content: [
          {
            type: 'text' as const,
            text: `**Error:** ${error instanceof Error ? error.message : 'Unknown error'}`,
          },
        ],
      };
    }
  },
});

// Helper function to extract user input from message
const extractUserInput = (lastMessage: any): string => {
  let userInput = '';
  if (lastMessage?.role === 'user' && lastMessage.content) {
    for (const part of lastMessage.content) {
      if (part.type === 'text') {
        userInput += part.text;
      }
    }
  }
  return userInput;
};

// Helper function to process tool calls
const processToolCall = (eventData: any): any | null => {
  // Check if it's the old format (AgentExecutionStep with tool_calls array)
  if (eventData.tool_calls && Array.isArray(eventData.tool_calls)) {
    const executionStep = eventData as AgentExecutionStep;
    if (executionStep.tool_calls && executionStep.tool_calls.length > 0) {
      const toolCall = executionStep.tool_calls[0]; // Process first tool call
      const parsedArgs = parseToolInput(toolCall.tool_input);
      return {
        type: 'tool-call' as const,
        toolCallId: `${executionStep.agent_name || 'agent'}_${toolCall.tool_name}_${Date.now()}`,
        toolName: toolCall.tool_name,
        args: parsedArgs,
        result: toolCall.tool_output,
      };
    }
  } else {
    // New format - individual tool event
    const toolEvent = eventData as ToolEvent;
    if (toolEvent.status === 'completed' && toolEvent.tool_name) {
      const parsedArgs = parseToolInput(toolEvent.input);
      return {
        type: 'tool-call' as const,
        toolCallId: `${toolEvent.run_id}_${toolEvent.tool_name}`,
        toolName: toolEvent.tool_name,
        args: parsedArgs,
        result: toolEvent.output || '',
      };
    }
  }
  return null;
};

// Helper function to parse tool input
const parseToolInput = (toolInput: string): any => {
  try {
    return JSON.parse(toolInput || '{}');
  } catch {
    try {
      // Convert Python dict syntax to JSON
      const jsonString = toolInput
        .replace(/'/g, '"')
        .replace(/True/g, 'true')
        .replace(/False/g, 'false')
        .replace(/None/g, 'null');
      return JSON.parse(jsonString);
    } catch {
      return { input: toolInput };
    }
  }
};

/**
 * Internal runtime component that gets recreated when history changes
 */
const ChatRuntimeInternal: FC<{
  projectId: number | null;
  historyMessages: any[];
  sessionId: string | null;
  clearHistory: () => Promise<void>;
  isLoadingHistory: boolean;
  children: React.ReactNode;
}> = ({ projectId, historyMessages, sessionId, clearHistory, isLoadingHistory, children }) => {
  const [pendingActions, setPendingActions] = useState<PendingChatAction[]>([]);
  const [processingActionId, setProcessingActionId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const generateInternalId = useCallback((): string => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `pending-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }, []);

  const ensureEnumValue = <T extends string>(
    values: readonly T[],
    value: string | undefined | null,
    fallback: T,
  ): T => {
    if (!value) {
      return fallback;
    }
    return values.includes(value as T) ? (value as T) : fallback;
  };

  const handleADRDefinitionConfirmation = useCallback(async (action: ChatAction): Promise<ActionExecutionResult> => {
    if (!projectId) {
      throw new Error('Select a project before adding ADR definitions.');
    }

    const rawContent = action.data?.content;
    if (!rawContent) {
      throw new Error('Missing ADR definition content.');
    }

    let parsedContent: Partial<ADRDefinitionUpsert & { id?: string; status?: string; source?: string }>;
    try {
      parsedContent = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;
    } catch (parseError) {
      throw new Error('Unable to parse ADR definition content.');
    }

    const definitionPayload: ADRDefinitionUpsert = {
      title: parsedContent.title ?? 'Untitled ADR',
      llmInstructions: parsedContent.llmInstructions ?? '',
      contextDescription: parsedContent.contextDescription ?? '',
      status: ensureEnumValue(
        Object.values(ADRDefinitionStatus) as ADRDefinitionStatus[],
        parsedContent.status,
        ADRDefinitionStatus.DEFINED,
      ),
      source: ensureEnumValue(
        Object.values(ADRSource) as ADRSource[],
        parsedContent.source,
        ADRSource.USER_CREATED,
      ),
    };

    const targetId = action.data?.target_id ?? parsedContent.id ?? null;
    const savedDefinition = await adrService.saveADRDefinition(projectId, definitionPayload, targetId);

    globalThis.dispatchEvent(new CustomEvent('adrDefinitionCreated', {
      detail: {
        definition: savedDefinition,
        projectId,
        source: 'chat',
      },
    }));

    return {
      message: targetId
        ? `ADR "${savedDefinition.title}" updated.`
        : `ADR "${savedDefinition.title}" added to your project.`,
    };
  }, [projectId]);

  const handleADRGeneration = useCallback(async (action: ChatAction): Promise<ActionExecutionResult> => {
    if (!projectId) {
      throw new Error('Select a project before generating ADR content.');
    }

    const adrId = action.data?.target_id;
    if (!adrId) {
      throw new Error('ADR ID is required for generation.');
    }

    try {
      // Dispatch event to notify UI that generation is starting
      globalThis.dispatchEvent(new CustomEvent('adrGenerationStarted', {
        detail: {
          adrId,
          projectId,
          source: 'chat',
        },
      }));

      // Trigger ADR generation - this will run the agent
      await adrService.generateADR(projectId, adrId);

      // Dispatch event to notify UI to refresh
      globalThis.dispatchEvent(new CustomEvent('adrContentGenerated', {
        detail: {
          adrId,
          projectId,
          source: 'chat',
        },
      }));

      return {
        message: 'ADR content generated successfully. You can now view and edit it in the ADR section.',
      };
    } catch (error) {
      // Dispatch event to notify UI that generation failed
      globalThis.dispatchEvent(new CustomEvent('adrGenerationFailed', {
        detail: {
          adrId,
          projectId,
          source: 'chat',
        },
      }));
      
      throw new Error(`Failed to generate ADR content: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [projectId]);

  const handleADRSuggestion = useCallback(async (action: ChatAction): Promise<ActionExecutionResult> => {
    if (!projectId) {
      throw new Error('Select a project before generating ADR suggestions.');
    }

    try {
      // Dispatch event to notify UI that suggestion generation is starting
      globalThis.dispatchEvent(new CustomEvent('adrSuggestionsStarted', {
        detail: {
          projectId,
          source: 'chat',
        },
      }));

      // Actually trigger the suggestion generation and wait for it to complete
      const { adrService } = await import('../../services/adrService');
      
      const request = {
        projectContext: 'Generate ADR suggestions based on project analysis',
        maxSuggestions: 4,
      };
      
      const response = await adrService.suggestADRs(projectId, request);

      // Dispatch event to notify UI that suggestions are ready, passing the suggestions and execution trace
      globalThis.dispatchEvent(new CustomEvent('adrSuggestionsCompleted', {
        detail: {
          projectId,
          source: 'chat',
          suggestions: response.suggestions,
          executionTrace: response.execution_trace,
        },
      }));

      return {
        message: 'ADR suggestions generated successfully. You can view them on the ADR Suggestions tab.',
      };
    } catch (error) {
      // Dispatch event to notify UI that generation failed
      globalThis.dispatchEvent(new CustomEvent('adrSuggestionsFailed', {
        detail: {
          projectId,
          source: 'chat',
        },
      }));
      
      throw new Error(`Failed to generate ADR suggestions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [projectId]);

  const handleSectionEdit = useCallback(async (action: ChatAction): Promise<ActionExecutionResult> => {
    if (!projectId) {
      throw new Error('Select a project before editing sections.');
    }

    const { section_id, instructions, project_id: actionProjectId } = action.data || {};
    
    
    if (!section_id || !instructions) {
      throw new Error('Section ID and instructions are required for editing.');
    }

    // Validate project ID matches
    if (actionProjectId && parseInt(String(actionProjectId), 10) !== projectId) {
      throw new Error('Section edit is for a different project.');
    }

    try {
      // Check if we're on the Generation page
      const isOnGenerationPage = location.pathname.includes('/generation');
      
      
      // If not on Generation page, navigate there first
      if (!isOnGenerationPage) {
        navigate(`/project/${projectId}/generation`);
        
        // Add a small delay to allow the page to mount before dispatching event
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Dispatch event to notify Generation page
      // The Generation page will auto-select the section and start editing
      globalThis.dispatchEvent(new CustomEvent('sectionEditStarted', {
        detail: {
          sectionId: section_id,
          projectId,
          instructions,
          source: 'chat',
          autoNavigated: !isOnGenerationPage,
          timestamp: action.data?.timestamp || new Date().toISOString(),
        },
      }));

      return {
        message: `Editing ${section_id}... ${!isOnGenerationPage ? 'Navigating to Generation page...' : 'Check the Generation page to see progress.'}`,
      };
    } catch (error) {
      logger.error('Error in handleSectionEdit:', error);
      
      // Dispatch failure event
      globalThis.dispatchEvent(new CustomEvent('sectionEditFailed', {
        detail: {
          sectionId: section_id,
          projectId,
          error: error instanceof Error ? error.message : 'Unknown error',
          source: 'chat',
        },
      }));
      
      throw new Error(`Failed to trigger section edit: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [projectId, navigate, location]);

  const handleConfirmationAction = useCallback(async (action: ChatAction): Promise<ActionExecutionResult> => {
    const targetAgent = action.data?.target_agent;
    switch (targetAgent) {
      case 'adr_create_confirmed':
        return handleADRDefinitionConfirmation(action);
      case 'adr_suggest_confirmed':
        return handleADRSuggestion(action);
      default:
        throw new Error(`Unsupported confirmation target: ${targetAgent ?? 'unknown'}`);
    }
  }, [handleADRDefinitionConfirmation, handleADRSuggestion]);

  const executeAction = useCallback(async (action: ChatAction): Promise<ActionExecutionResult> => {
    if (action.type === 'confirmation') {
      return handleConfirmationAction(action);
    }

    if (action.type === 'adr_generate') {
      return handleADRGeneration(action);
    }

    if (action.type === 'adr_suggest') {
      return handleADRSuggestion(action);
    }

    if (action.type === 'section_edit_triggered') {
      return handleSectionEdit(action);
    }

    logger.warn('Unhandled action received:', action);
    return {};
  }, [handleConfirmationAction, handleADRGeneration, handleADRSuggestion, handleSectionEdit]);

  const handleIncomingActions = useCallback((actions: ChatAction[]) => {
    
    if (!actions || actions.length === 0) {
      return;
    }

    const confirmable = actions.filter(action => action.confirmation_required);
    const autoExecutable = actions.filter(action => !action.confirmation_required && action.auto_execute);

    if (confirmable.length > 0) {
      setPendingActions(prev => [
        ...prev,
        ...confirmable.map(action => ({ ...action, internalId: generateInternalId() })),
      ]);
    }

    autoExecutable.forEach(action => {
      executeAction(action).catch(error => {
        logger.error('Automatic action execution failed:', error);
      });
    });
  }, [executeAction, generateInternalId]);

  const confirmPendingAction = useCallback(async (action: PendingChatAction): Promise<ActionExecutionResult> => {
    setProcessingActionId(action.internalId);
    try {
      const result = await executeAction(action);
      setPendingActions(prev => prev.filter(item => item.internalId !== action.internalId));
      return result;
    } finally {
      setProcessingActionId(null);
    }
  }, [executeAction]);

  const dismissPendingAction = useCallback((action: PendingChatAction) => {
    setPendingActions(prev => prev.filter(item => item.internalId !== action.internalId));
  }, []);

  const actionsContextValue = useMemo<ChatActionsContextValue>(() => ({
    pendingConfirmations: pendingActions,
    confirmPendingAction,
    dismissPendingAction,
    processingActionId,
  }), [confirmPendingAction, dismissPendingAction, pendingActions, processingActionId]);

  const chatAdapter = useMemo(
    () => createChatAdapter(projectId, sessionId, handleIncomingActions),
    [projectId, sessionId, handleIncomingActions],
  );
  
  const runtime = useLocalRuntime(chatAdapter, {
    initialMessages: historyMessages,
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ChatActionsContext.Provider value={actionsContextValue}>
        <ChatHistoryContext.Provider value={{ clearHistory, isLoadingHistory }}>
          {/* Tool UI Components - Register all backend tools here for visual cards in chat */}
          {/* If a new tool is added to the backend, it will display with a generic gray card */}
          {/* until you import and add its specific UI component here using createToolUI() */}
          <SearchWikiToolUI />
          <SearchGuidelinesToolUI />
          <SearchUserDocsToolUI />
          <SearchBrownfieldDocsToolUI />
          <ReadBusinessRequirementsToolUI />
          <ReadGeneratedContentToolUI />
          <ReadProjectContextToolUI />
          <LookupApplicationIdsToolUI />
          <ReadProjectADRsToolUI />
          <ReadProjectInstructionsToolUI />
          <FetchWebpageToolUI />
          <SelectProjectToolUI />
          {children}
        </ChatHistoryContext.Provider>
      </ChatActionsContext.Provider>
    </AssistantRuntimeProvider>
  );
};

export const ChatRuntimeProvider: FC<ChatRuntimeProviderProps> = ({ 
  children, 
  projectId = null,
}) => {
  const { historyMessages, isLoadingHistory, sessionId, clearHistory } = useChatHistoryRestore(projectId);
  const [resetKey, setResetKey] = useState(0);
  
  // Load project context (including title) for the current project
  useProjectContext(projectId);
  
  // Enhanced clear history function that forces component recreation
  const clearHistoryAndRuntime = async () => {
    await clearHistory();
    // Force recreation of the runtime component
    setResetKey(prev => prev + 1);
  };

  // Create a key that changes when we need to recreate the runtime
  const runtimeKey = `${projectId}-${resetKey}-${isLoadingHistory ? 'loading' : historyMessages.length}`;

  return (
    <ChatRuntimeInternal
      key={runtimeKey}
      projectId={projectId}
      historyMessages={historyMessages}
      sessionId={sessionId}
      clearHistory={clearHistoryAndRuntime}
      isLoadingHistory={isLoadingHistory}
    >
      {children}
    </ChatRuntimeInternal>
  );
};
